import { AppearanceTimeline } from "./ui/OutreachTimeline";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { User, Building2, Star, Linkedin, Pencil, Crown, UserMinus } from "lucide-react";
import { getPrimeContact, setRfpPrimeContactHighValue, type PrimeContactDetail, type PrimeAppearance, type RfpPrimeContact, type PrimeContact } from "./api/CrmApi";
import FlagPrimeContactModal from "./ui/FlagPrimeContactModal";
import LogReplyModal from "./ui/LogReplyModal";
import SetPrimeContactStatusModal from "./ui/SetPrimeContactStatusModal";
import PrimeContactStatusDropdown, { type PrimeContactStatus } from "./ui/PrimeContactStatusDropdown";
import EditPrimeContactModal from "./ui/EditPrimeContactModal";
import HighValueTooltip from "./ui/HighValueTooltip";
import ReplyTooltip from "./ui/ReplyTooltip";
import { ensureHttpUrl } from "./ui/crmUi";
import ContractorTypeBadge from "./ui/ContractorTypeBadge";
import PageHeader from "shared/PageHeader";
import RemoveContactModal from "./ui/RemoveContactModal";
import { useBackTo } from "shared/listNavigation";
import Button from "shared/Button";
import StatusPill from "shared/StatusPill";
import Alert from "shared/Alert";

export default function PrimeContactProfilePage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const crmBasePath = location.pathname.startsWith("/employee") ? "/employee/crm" : "/admin/crm";
  // Back returns to wherever this profile was opened from (Prime Contacts page
  // N with its filters, a Company page, the review queue) and highlights the row.
  const backTo = useBackTo(`${crmBasePath}/prime-contacts`, "Back to Prime Contacts", id ? Number(id) : undefined);

  const [prime, setPrime] = useState<PrimeContactDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [flagTarget, setFlagTarget] = useState<RfpPrimeContact | null>(null);
  const [replyTarget, setReplyTarget] = useState<RfpPrimeContact | null>(null);
  const [statusTarget, setStatusTarget] = useState<PrimeContactStatus | null>(null);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [actionError, setActionError] = useState("");

  const load = () => {
    if (!id) return;
    setLoading(true);
    setError("");
    getPrimeContact(Number(id))
      .then(setPrime)
      .catch((e) => setError(e.response?.data?.detail || "Failed to load contact"))
      .finally(() => setLoading(false));
  };

  useEffect(load, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // FlagPrimeContactModal/LogReplyModal edit a specific RfpPrimeContact
  // (appearance) row in place server-side, then hand back the updated row —
  // patch it into this Prime's own appearances list rather than a full reload.
  const handleAppearanceSaved = (updated: RfpPrimeContact) => {
    setPrime((prev) =>
      prev
        ? { ...prev, appearances: prev.appearances.map((a) => (a.id === updated.id ? { ...a, ...updated } : a)) }
        : prev
    );
  };

  const handleUnflag = async (appearance: PrimeAppearance) => {
    try {
      const updated = await setRfpPrimeContactHighValue(appearance.id, false);
      handleAppearanceSaved(updated);
    } catch (e: any) {
      setActionError(e.response?.data?.detail || "Failed to unflag");
    }
  };

  const handleStatusSaved = (updated: PrimeContact) => {
    setPrime((prev) => (prev ? { ...prev, ...updated } : prev));
  };

  const handleEditSaved = (updated: PrimeContact) => {
    const emailChanged = !!prime && updated.primary_email !== prime.primary_email;
    setPrime((prev) => (prev ? { ...prev, ...updated } : prev));
    // A new email re-links the company and rewrites the appearance rows'
    // email — reload so those sections don't show stale data.
    if (emailChanged) load();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !prime) {
    return <Alert tone="danger">{error || "Contact not found"}</Alert>;
  }

  const name = [prime.first_name, prime.last_name].filter(Boolean).join(" ") || prime.primary_email;

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        backTo={backTo}
        title={
          <span className="inline-flex items-center gap-3 min-w-0">
            <span className="flex-shrink-0 w-10 h-10 rounded-xl bg-primary-soft flex items-center justify-center">
              <User className="w-5 h-5 text-primary" />
            </span>
            {name}
          </span>
        }
        badge={
          <>
            <PrimeContactStatusDropdown status={prime.status} onSelect={setStatusTarget} />
            {prime.is_high_value && <Star className="w-4 h-4 text-warning fill-warning" />}
            {prime.is_primary && (
              <StatusPill tone="warning" size="sm" title="This company's primary contact — outreach uses this person first">
                <Crown className="w-3 h-3 inline -mt-0.5 mr-1" />Primary contact
              </StatusPill>
            )}
          </>
        }
        description={
          <span className="flex items-center gap-3 flex-wrap">
            <a href={`mailto:${prime.primary_email}`} className="text-sm text-primary hover:underline">{prime.primary_email}</a>
            {prime.title && <span className="text-sm text-foreground">{prime.title}</span>}
            {prime.phone && <span className="text-sm text-muted-foreground">{prime.phone}</span>}
            {prime.personal_linkedin_url ? (
              <a
                href={ensureHttpUrl(prime.personal_linkedin_url)}
                target="_blank"
                rel="noreferrer"
                title="Personal LinkedIn"
                className="text-info hover:text-info/80"
              >
                <Linkedin className="w-3.5 h-3.5" />
              </a>
            ) : (
              <span className="text-xs text-muted-foreground italic">LinkedIn profile not found</span>
            )}
          </span>
        }
        actions={
          <>
            <Button variant="brand-outline" size="sm" onClick={() => setEditOpen(true)} title="Edit contact details">
              <Pencil className="w-3.5 h-3.5" /> Edit
            </Button>
            <Button variant="danger" size="sm" onClick={() => setRemoveOpen(true)} title="Remove this contact and their RFP records (the company stays)">
              <UserMinus className="w-3.5 h-3.5" /> Remove
            </Button>
          </>
        }
      />

      {prime.company ? (
        // A plain div driving navigation via useNavigate, not <Link> — the
        // LinkedIn icon below is a real <a>, and nesting an <a> inside a
        // <Link> (also an <a> under the hood) is invalid HTML that React
        // flags as a hydration error.
        <div
          role="link"
          tabIndex={0}
          onClick={() => navigate(`${crmBasePath}/companies/${prime.company!.id}`)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") navigate(`${crmBasePath}/companies/${prime.company!.id}`);
          }}
          className="flex items-center gap-2 w-fit px-3 py-2 rounded-lg border border-border bg-surface hover:bg-surface-muted transition-colors cursor-pointer"
        >
          <Building2 className="w-4 h-4 text-primary" />
          <span className="text-sm text-foreground">{prime.company.name || "(unnamed company)"}</span>
          {/* The company's own canonical LinkedIn page — a company page, not
              this person's individual profile, so it's placed and labeled
              here rather than next to their own name. */}
          {prime.company.linkedin_url && (
            <a
              href={ensureHttpUrl(prime.company.linkedin_url)}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              title="Company LinkedIn"
              className="text-info hover:text-info/80"
            >
              <Linkedin className="w-3.5 h-3.5" />
            </a>
          )}
          {prime.company.naics_codes.length > 0 && (
            <span className="text-xs font-mono text-success-soft-foreground">NAICS {prime.company.naics_codes.join(", ")}</span>
          )}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">Not linked to a company (older data — contacts now need a company).</p>
      )}

      {prime.human_review_status && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-surface w-fit">
          <StatusPill
            tone={prime.human_review_status === "verified" ? "success" : prime.human_review_status === "rejected" ? "danger" : "warning"}
            dot
          >
            {prime.human_review_status === "review_needed" ? "Named-email review pending" : `Named-email ${prime.human_review_status}`}
          </StatusPill>
          {prime.secondary_email && <span className="text-xs text-muted-foreground">Other: {prime.secondary_email}</span>}
          {prime.human_review_status === "review_needed" && (
            <Link to={`${crmBasePath}/prime-contacts/review-queue`} className="text-xs text-primary hover:underline">
              Review it
            </Link>
          )}
        </div>
      )}

      {actionError && <Alert tone="danger">{actionError}</Alert>}

      <div>
        <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-2">
          RFP Appearances <StatusPill tone="neutral" size="sm">{prime.appearances.length}</StatusPill>
        </p>
        <div className="flex flex-col gap-2">
          {prime.appearances.map((a: PrimeAppearance) => (
            <div key={a.id} className="rounded-xl border border-border bg-surface px-4 py-3">
              <div className="flex items-center justify-between gap-3">
                <Link to={`${crmBasePath}?opportunity_id=${a.opportunity_id}`} className="text-sm text-primary hover:underline">
                  {a.opportunity_title}
                </Link>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <HighValueTooltip contact={a} onFlag={() => setFlagTarget(a)} onUnflag={() => handleUnflag(a)} />
                  <ReplyTooltip contact={a} onClick={() => setReplyTarget(a)} />
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1.5 flex-wrap">
                <span title="The company's role on this RFP">{a.prime_company_id ? "Company's role:" : "Role:"}</span>
                <ContractorTypeBadge type={a.effective_contractor_type} />
                <span>· {[a.city, a.state].filter(Boolean).join(", ") || "—"}</span>
                {a.plan_holder_status && <span>· {a.plan_holder_status}</span>}
              </p>
              {a.reply_text && (
                <p className="text-xs text-foreground mt-2 bg-surface-muted rounded-lg px-3 py-2">{a.reply_text}</p>
              )}
              <AppearanceTimeline opportunityId={a.opportunity_id} companyId={a.prime_company_id} />
            </div>
          ))}
          {prime.appearances.length === 0 && (
            <p className="text-sm text-muted-foreground px-4 py-8 text-center rounded-xl border border-border bg-surface">
              No RFP appearances on file.
            </p>
          )}
        </div>
      </div>

      {flagTarget && (
        <FlagPrimeContactModal contact={flagTarget} onClose={() => setFlagTarget(null)} onSaved={handleAppearanceSaved} />
      )}
      {replyTarget && (
        <LogReplyModal contact={replyTarget} onClose={() => setReplyTarget(null)} onSaved={handleAppearanceSaved} />
      )}
      {statusTarget && (
        <SetPrimeContactStatusModal
          email={prime.primary_email}
          displayName={name}
          currentStatus={prime.status}
          targetStatus={statusTarget}
          onClose={() => setStatusTarget(null)}
          onSaved={handleStatusSaved}
        />
      )}
      {editOpen && (
        <EditPrimeContactModal prime={prime} onClose={() => setEditOpen(false)} onSaved={handleEditSaved} />
      )}
      {removeOpen && (
        <RemoveContactModal
          contactId={prime.id}
          onClose={() => setRemoveOpen(false)}
          // the person no longer exists: go to their company (or the contacts list when they somehow have none), never back to this page
          onRemoved={(result) => navigate(result.company_id ? `${crmBasePath}/companies/${result.company_id}` : `${crmBasePath}/prime-contacts`, { replace: true })}
        />
      )}
    </div>
  );
}
